import { useMutation } from '@apollo/client';
import { useCallback, useState } from 'react';

import {
  ExpertRequestCloseReasonCode,
  ExpertRequestTransitionAction,
} from '@/__generated__/graphql';
import { ERROR_CODES, hasErrorCode } from '@/core/api';
import { EXPERT_REQUEST__UPDATE, fetchExpertRequest } from '@/expertrequest/store';
import { TRANSITION_EXPERT_REQUEST } from '@/expertrequest/store/queries';
import { useAppDispatch } from '@/store';

export interface HoldInput {
  holdReason: string;
  holdResumeDate: string;
}

export interface CloseInput {
  closeReasonCode: ExpertRequestCloseReasonCode;
  closeReason?: string;
}

/** The transition whose confirm modal is currently open (null = none). */
export type OpenTransitionModal = ExpertRequestTransitionAction | null;

export interface UseErTransitionsResult {
  openModal: OpenTransitionModal;
  error: string | null;
  submitting: boolean;
  openTransition: (action: ExpertRequestTransitionAction) => void;
  dismiss: () => void;
  submitForSourcing: () => void;
  resume: () => void;
  reopen: () => void;
  putOnHold: (input: HoldInput) => void;
  closeRequest: (input: CloseInput) => void;
}

export function messageForError(err: unknown): string {
  if (hasErrorCode(err, ERROR_CODES.EXPERT_REQUEST_STAGE_CONFLICT)) {
    return 'This request was changed by someone else. We refreshed it; please try again.';
  }
  if (hasErrorCode(err, ERROR_CODES.EXPERT_REQUEST_INVALID_STAGE_TRANSITION)) {
    return 'That change can’t be made from the request’s current status.';
  }
  if (hasErrorCode(err, ERROR_CODES.EXPERT_REQUEST_INVALID_CLOSE_REASON)) {
    return 'Please choose a valid reason for closing.';
  }
  if (hasErrorCode(err, ERROR_CODES.EXPERT_REQUEST_CLOSE_REASON_TOO_LONG)) {
    return 'That close reason is too long. Please shorten it.';
  }
  if (hasErrorCode(err, ERROR_CODES.EXPERT_REQUEST_HAS_OPEN_CONSULTATIONS)) {
    return 'This expert request has open consultations. Close it after consultations are finalized.';
  }
  if (hasErrorCode(err, ERROR_CODES.PERMISSION_NOT_ALLOWED)) {
    return 'You don’t have permission to do that.';
  }
  return 'Something went wrong. Please try again.';
}

/**
 * useErTransitions — single owner of the ER lifecycle transition flow (ENG-5290
 * T2, D-T2-7). Holds which confirm modal is open, the in-flight flag, and the
 * error message; runs `transitionExpertRequest`; and re-dispatches
 * `fetchExpertRequest` on success AND on `stage_conflict` (the ER is
 * Redux-resident, not Apollo-cached, so an Apollo refetch alone wouldn't update
 * the page). On failure the modal stays open with an inline error.
 */
export function useErTransitions(expertRequestId: string): UseErTransitionsResult {
  const dispatch = useAppDispatch();
  const [transition, { loading }] = useMutation(TRANSITION_EXPERT_REQUEST);
  const [openModal, setOpenModal] = useState<OpenTransitionModal>(null);
  const [error, setError] = useState<string | null>(null);

  const openTransition = useCallback((action: ExpertRequestTransitionAction) => {
    setError(null);
    setOpenModal(action);
  }, []);

  const dismiss = useCallback(() => {
    setOpenModal(null);
    setError(null);
  }, []);

  const run = useCallback(
    async (
      action: ExpertRequestTransitionAction,
      vars: {
        hold_reason?: string;
        hold_resume_date?: string;
        close_reason_code?: ExpertRequestCloseReasonCode;
        close_reason?: string;
      } = {}
    ) => {
      setError(null);
      try {
        const { data } = await transition({
          variables: { id: expertRequestId, action, ...vars },
        });
        // Reflect the new stage/fields immediately from the mutation result, so
        // the UI updates even if the heavier full refetch below fails (e.g. a
        // transient consultation/attachment load error must not strand the user
        // on stale data and force a manual refresh).
        const updated = data?.transitionExpertRequest;
        if (updated) {
          dispatch({ type: EXPERT_REQUEST__UPDATE, expertRequest: updated });
        }
        setOpenModal(null);
        // Best-effort refresh of candidates/consultations; a failure here must
        // not mask the successful transition already reflected above.
        try {
          await dispatch(fetchExpertRequest(expertRequestId));
        } catch {
          /* stage already reflected from the mutation result */
        }
      } catch (err) {
        // A lost CAS race: land the user on fresh data, keep the modal open.
        if (hasErrorCode(err, ERROR_CODES.EXPERT_REQUEST_STAGE_CONFLICT)) {
          try {
            await dispatch(fetchExpertRequest(expertRequestId));
          } catch {
            /* best-effort refresh */
          }
        }
        setError(messageForError(err));
      }
    },
    [transition, dispatch, expertRequestId]
  );

  return {
    openModal,
    error,
    submitting: loading,
    openTransition,
    dismiss,
    submitForSourcing: () => run(ExpertRequestTransitionAction.SubmitForSourcing),
    resume: () => run(ExpertRequestTransitionAction.Resume),
    reopen: () => run(ExpertRequestTransitionAction.Reopen),
    putOnHold: ({ holdReason, holdResumeDate }: HoldInput) =>
      run(ExpertRequestTransitionAction.PutOnHold, {
        hold_reason: holdReason,
        hold_resume_date: holdResumeDate,
      }),
    closeRequest: ({ closeReasonCode, closeReason }: CloseInput) =>
      run(ExpertRequestTransitionAction.Close, {
        close_reason_code: closeReasonCode,
        close_reason: closeReason,
      }),
  };
}
